import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import { z } from 'zod';

import { industryValues } from '@/services/resume-checker';
import { asArray } from '@/utils/asArray';

export enum SeniorityLevel {
  INTERN = 'INTERN',
  JUNIOR = 'JUNIOR',
  MID = 'MID',
  SENIOR = 'SENIOR',
}

export enum WorkType {
  FULL_TIME = 'FULL_TIME',
  PART_TIME = 'PART_TIME',
  INTERNSHIP = 'INTERNSHIP',
  FREELANCER = 'FREELANCER',
  CONSULTANT = 'CONSULTANT',
}

export enum Ordering {
  CREATED_AT = 'created_at',
  '-CREATED_AT' = '-created_at',
  SALARY_LOWER = 'salary_lower',
  '-SALARY_LOWER' = '-salary_lower',
}

export const orderingOptions = [
  { label: 'Newest Post', value: Ordering['-CREATED_AT'] },
  { label: 'Oldest Post', value: Ordering.CREATED_AT },
  { label: 'Highest Salary', value: Ordering['-SALARY_LOWER'] },
  { label: 'Lowest Salary', value: Ordering.SALARY_LOWER },
];

export enum SalaryPeriod {
  DAY = 'DAY',
  HOUR = 'HOUR',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

const paramsSchema = z.object({
  search: z.string().optional().catch(undefined),
  location: z.string().optional().catch(undefined),
  ordering: z.nativeEnum(Ordering).optional().catch(undefined),
  seniorityLevel: asArray(z.nativeEnum(SeniorityLevel))
    .optional()
    .catch(undefined),
  workType: asArray(z.nativeEnum(WorkType)).optional().catch(undefined),
  industry: z.enum(industryValues).optional().catch(undefined),
  company: asArray(z.string()).optional().catch(undefined),
  company_name: z.string().optional().catch(undefined),
  salaryLower: z.coerce.number().optional().catch(undefined),
  salaryUpper: z.coerce.number().optional().catch(undefined),
});

export type DashboardParams = z.infer<typeof paramsSchema>;

const getQueryParams = (query: ParsedUrlQuery) => paramsSchema.parse(query);

const withoutEmptyValues = (query: ParsedUrlQuery): ParsedUrlQuery =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) =>
      Array.isArray(value)
        ? value.length > 0
        : value !== undefined && value !== ''
    )
  );

export const useJobBoardParams = () => {
  const router = useRouter();
  const params = getQueryParams(router.query);

  const setParams = ({
    salaryLower,
    salaryUpper,
    ...newParams
  }: Partial<DashboardParams>) => {
    if (!router.isReady) return;
    router.replace(
      {
        query: withoutEmptyValues({
          ...router.query,
          ...newParams,
          salaryLower: salaryLower ? salaryLower.toString() : undefined,
          salaryUpper: salaryUpper ? salaryUpper.toString() : undefined,
        }),
      },
      undefined,
      { shallow: true }
    );
  };

  const deleteParam = (
    key: string,
    value: DashboardParams[keyof DashboardParams]
  ) => {
    const { [key]: valuesToFiltrate, ...newParams } = router.query;
    if (!valuesToFiltrate) return;
    if (Array.isArray(valuesToFiltrate)) {
      const newFiltredValues = valuesToFiltrate.filter(
        (singleValue) => singleValue !== value
      );
      return router.replace(
        {
          query: {
            [key]: newFiltredValues,
            ...newParams,
          },
        },
        undefined,
        { shallow: true }
      );
    }

    if (valuesToFiltrate === value) {
      return router.replace(
        {
          query: newParams,
        },
        undefined,
        { shallow: true }
      );
    }
  };

  const resetParams = () => {
    if (!router.isReady) return;
    router.replace({ query: undefined }, undefined, { shallow: true });
  };
  const orderingOption = orderingOptions.find(
    (option) => option.value === (params.ordering ?? Ordering['-CREATED_AT'])
  );

  return {
    setParams,
    resetParams,
    deleteParam,
    params,
    orderingOption,
    isReady: router.isReady,
  };
};
